import { z } from "zod";

/**
 * env is the typed, build-time client configuration. Vite inlines
 * `import.meta.env.VITE_*` into the bundle at `vite build` (the same process in
 * dev and prod — no runtime injection, no window.__ENV__). The raw VITE_ keys
 * are typed in vite-env.d.ts; this object re-exposes them in lowerCamelCase so
 * call sites never touch `import.meta.env` directly. Optional keys carry
 * `|| undefined` and surface as `undefined` when unset; every required key is
 * validated as non-empty at module load by assertClientEnv() below, so a real
 * dev or production build with a missing required var fails loudly at boot
 * rather than shipping a silent "" (the lazy requireOrySdkUrl() guard stays for
 * the call sites that read that URL directly).
 */
export const env = {
  apiBaseUrl: import.meta.env.VITE_HTTP_INTERNAL_API_URL,
  // The public origin the embed iframe snippet + preview point at. The copyable
  // snippet a publisher pastes into their site must carry an absolute,
  // publicly-reachable origin (window.location.origin is only right for the in-app
  // preview). VITE_APP_BASE_URL wins when set, so the origin is environment-driven
  // (local dev server, a preview deploy, production) rather than hardcoded; it falls
  // back to the DEV-split default when unset. Inlined by Vite at build the same way
  // DEV is.
  appBaseUrl:
    import.meta.env.VITE_APP_BASE_URL ||
    (import.meta.env.DEV ? "http://localhost:4000" : "https://app.mountaya.com"),
  bucketRoutesUrl: import.meta.env.VITE_BUCKET_ROUTES_URL || undefined,
  dataApiUrl: import.meta.env.VITE_HTTP_DATA_API_URL,
  docsUrl: import.meta.env.VITE_DOCS_URL || undefined,
  googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || undefined,
  orySdkUrl: import.meta.env.VITE_ORY_SDK_URL,
  posthogHost: import.meta.env.VITE_POSTHOG_HOST || undefined,
  posthogProjectToken: import.meta.env.VITE_POSTHOG_PROJECT_TOKEN || undefined,
  rootDomain: import.meta.env.VITE_ROOT_DOMAIN,
  rootOrganizationId: import.meta.env.VITE_ROOT_ORGANIZATION_ID || undefined,
  rootPublishableKey: import.meta.env.VITE_ROOT_PUBLISHABLE_KEY || undefined,
  // The Stripe Payment Method Configuration the organization card flow mounts its
  // Elements against. It must be the SAME configuration the backend routes its
  // SetupIntent through (STRIPE_PMC_ID_ORGANIZATION), or the deferred confirm can
  // offer a method the intent does not accept. Unset on both sides falls back to
  // the Stripe account default, which is what local dev runs on.
  stripePaymentMethodConfigurationId: import.meta.env.VITE_STRIPE_PMC_ID_ORGANIZATION || undefined,
  stripePublicKey: import.meta.env.VITE_STRIPE_PUBLIC_KEY || undefined,
  tileApiUrl: import.meta.env.VITE_HTTP_TILE_API_URL,
} as const;

/**
 * requiredClientEnvSchema lists the build-time vars the app cannot run correctly
 * without, each validated as non-empty. Vite inlines an unset var as "", so this
 * catches a forgotten var rather than shipping a silent "". VITE_ROOT_ORGANIZATION_ID
 * is required here so the root-organization gates (the embed menu item, the
 * writable-org resolution) can never fail open on an undefined root id.
 */
export const requiredClientEnvSchema = z.object({
  VITE_HTTP_DATA_API_URL: z.string().min(1),
  VITE_HTTP_INTERNAL_API_URL: z.string().min(1),
  VITE_HTTP_TILE_API_URL: z.string().min(1),
  VITE_ORY_SDK_URL: z.string().min(1),
  VITE_ROOT_DOMAIN: z.string().min(1),
  VITE_ROOT_ORGANIZATION_ID: z.string().min(1),
});

/**
 * assertClientEnv validates the required client env once at module load. The test
 * runner runs with an intentionally unset client env (the data layer is mocked and
 * never reached), so MODE "test" is exempt; every real dev or production build with
 * a missing required var throws here at boot, naming each missing var, rather than
 * failing later with a silent "".
 */
const assertClientEnv = (): void => {
  if (import.meta.env.MODE === "test") {
    return;
  }

  const result = requiredClientEnvSchema.safeParse(import.meta.env);
  if (!result.success) {
    const missing = result.error.issues.map((issue) => issue.path.join(".")).sort();

    throw new Error(`Missing required client env (build-time Vite): ${missing.join(", ")}`);
  }
};

assertClientEnv();

/**
 * requireOrySdkUrl returns the Ory SDK URL from the build-time env or throws. A
 * loud failure at boot beats a silent localhost fallback that would ship to
 * prod the day the env is forgotten. Reads VITE_ORY_SDK_URL, inlined by Vite —
 * so a missing value fails the same way in dev and in the built image.
 */
export const requireOrySdkUrl = (): string => {
  if (!env.orySdkUrl) {
    throw new Error("VITE_ORY_SDK_URL is not set (build-time Vite client env)");
  }

  return env.orySdkUrl;
};
